<template>
  <div class="exhibition-invitation" :class="{ 'is-open': open }" @pointerenter="reveal" @pointerleave="conceal" @focusin="reveal" @focusout="onFocusOut" @keydown.esc.stop.prevent="open = false">
    <ViewPresentationControl value="article" :collection="collection" :loading="loading" @change="$emit('enter')"/>
    <button id="exhibition-invitation-preview" class="invitation-preview" type="button" :tabindex="open ? 0 : -1" :aria-hidden="!open" :disabled="loading" @click="$emit('enter')">
      <span class="invitation-preview-space">
        <img v-if="view.illustration?.src" :src="view.illustration.src" crossorigin="anonymous" alt="" />
        <span ref="scene" class="invitation-preview-scene" :class="{ 'is-ready': ready }" aria-hidden="true"></span>
      </span>
      <span class="invitation-preview-copy"><span>{{ view.title }}</span><span class="invitation-action">{{collection?'Visit the collection':'Visit this View'}} <span aria-hidden="true"><ArrowUpRightIcon data-exhibition-icon /></span></span></span>
    </button>
  </div>
</template>

<script>
import ViewPresentationControl from './ViewPresentationControl.vue';
import { museumSession, ownMuseumViews } from '../../utils/viewMuseumSession';
export default {
  components: { ViewPresentationControl },
  props: { view: { type: Object, required: true }, sources: { type: Map, default:()=>new Map() }, collection:Boolean, createdAt:{type:[String,Number],default:null}, language:{type:String,default:''}, collectionViews:{type:Array,default:()=>[]}, shareId:{type:String,default:''}, getPosition:Function, loading: Boolean },
  emits: ['enter'],
  data: () => ({ open: false, ready: false }),
  mounted() {
    this._scroll = () => { if(this.open && !this._scrollFrame)this._scrollFrame=requestAnimationFrame(()=>{this._scrollFrame=0;this.syncPreview();}); };
    window.addEventListener('scroll',this._scroll,{passive:true});
    this._dark = document.body.classList.contains('dark-mode');
    this._theme = new MutationObserver(() => {
      const dark = document.body.classList.contains('dark-mode');
      if (dark === this._dark) return;
      this._dark = dark;
      this._scene?.setTheme(dark);
    });
    this._theme.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  },
  beforeUnmount() { this._closed = true; window.removeEventListener('scroll',this._scroll);cancelAnimationFrame(this._scrollFrame); clearTimeout(this._concealTimer); this._theme?.disconnect(); this._scene?.dispose(); },
  methods: {
    async reveal() {
      if (!window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches) return;
      clearTimeout(this._concealTimer); this.open = true;
      if (this._scene) { this.syncPreview(); return; }
      if (this._preparing) return;
      this._preparing = true;
      try {
        if(this.collection){
          const {createMuseumScene}=await import('../../utils/viewMuseumScene');if(this._closed)return;
          this._scene=createMuseumScene(this.$refs.scene,{preview:true,dark:document.body.classList.contains('dark-mode'),onReady:()=>{this.ready=true;}});
          this.syncPreview();return;
        }
        const [{ createExhibitionScene }, mapping, {default:guides}] = await Promise.all([
          import(/* webpackChunkName: "view-exhibition" */ '../../utils/exhibitionScene'),
          import('../../utils/viewExhibition'),
          import('../../data/exhibitionGuides.json'),
        ]);
        if (this._closed) return;
        this._mapping=mapping;
        const guide=guides[this.shareId];
        this._exhibition = mapping.createViewExhibition(this.view, id => this.sources.get(id),guide?.question===this.view.question?guide:null);
        this._scene = createExhibitionScene(this.$refs.scene, this._exhibition, {
          preview: true, dark: document.body.classList.contains('dark-mode'), createdAt:this.createdAt, language:this.language,
          onReady: () => { this.ready = true; }, onError: () => { this.ready = false; },
        });
        this.syncPreview();
      } catch { this.ready = false; } finally { this._preparing = false; }
    },
    syncPreview() {
      if(!this._scene)return;
      if(this.collection){
        const state=museumSession(),saved=state.hall||{},own=ownMuseumViews();
        const selected=[...own,...this.collectionViews].find(view=>view.id===(saved.selectedId||state.lastView?.id))||this.collectionViews[0]||this.view;
        const key=JSON.stringify([selected.id,selected.title,selected.illustration?.src]);
        if(this._selectionKey!==key){this._selectionKey=key;this._scene.setExhibition(selected);}
        const catalogue=saved.collectionScope==='own'&&own.length?own:this.collectionViews;
        this._scene.setCatalogue(catalogue.slice(0,4),{totalPages:Math.max(1,Math.ceil(catalogue.length/4))});
        const area=saved.area||'hall';
        if(this._area!==area){this._area=area;this._scene.go(area,{instant:true});}
        return;
      }
      const position=this.getPosition?.();
      const key=JSON.stringify(position);if(this._positionKey===key)return;this._positionKey=key;
      if(!position){this._scene.go(-1,{instant:true});return;}
      const step=this._exhibition.steps[this._mapping.exhibitionStepForPosition(this._exhibition,position.sectionIndex,position.blockIndex)];
      const index=step?step.kind==='closing'?-2:step.kind==='citations'?-4:step.stopIndex:this._mapping.exhibitionStopForPosition(this._exhibition,position.sectionIndex,position.blockIndex);
      this._scene.go(index,{instant:true,roomIndex:step?.roomIndex??null});
    },
    conceal() { clearTimeout(this._concealTimer); this._concealTimer = setTimeout(() => { if (!this.$el.contains(document.activeElement) && !this.$el.matches(':hover')) this.open = false; }, 80); },
    onFocusOut(event) { if (!this.$el.contains(event.relatedTarget) && !this.$el.matches(':hover')) this.open = false; },
  },
};
</script>

<style scoped>
.exhibition-invitation { --invitation-paper: 255,255,255; --invitation-ink: #505050; --invitation-edge: rgba(0,0,0,.09); --ex-paper:rgb(var(--invitation-paper)); --ex-ink:var(--invitation-ink); position: fixed; right: clamp(18px,2.4vw,30px); bottom: calc(22px + env(safe-area-inset-bottom)); z-index: 900; font-family: 'Quattrocento Sans',sans-serif; color: var(--invitation-ink); }
:global(body.dark-mode .exhibition-invitation) { --invitation-paper: 37,37,39; --invitation-ink: #cfcfd2; --invitation-edge: rgba(255,255,255,.13); }
.exhibition-invitation button { appearance: none; font: inherit; color: inherit; cursor: var(--site-interactive-cursor); }
.exhibition-invitation button:focus-visible { outline: 1px solid currentColor; outline-offset: 5px; }
.invitation-preview:disabled { opacity: .5; }
.invitation-preview { position: absolute; right: calc(100% + 10px); bottom: 0; width: 330px; padding: 7px; text-align: left; border: 1px solid var(--invitation-edge); border-radius: 10px; background: rgba(var(--invitation-paper),.78); backdrop-filter: blur(22px); -webkit-backdrop-filter: blur(22px); box-shadow: 0 8px 35px #0000000c, inset 0 1px 0 #ffffff18; opacity: 0; visibility: hidden; transform: translateX(16px); transition: opacity 160ms ease, transform 220ms cubic-bezier(.23,1,.32,1), visibility 220ms; }
.invitation-preview::after { content: ''; position: absolute; top: 0; bottom: 0; right: -12px; width: 12px; }
.is-open .invitation-preview { opacity: 1; visibility: visible; transform: translateX(0); }
.invitation-preview-space { display: block; position: relative; aspect-ratio: 1.75; overflow: hidden; border-radius: 5px; background: #dfddd4; }
.invitation-preview-space img { width: 100%; height: 100%; object-fit: cover; }
.invitation-preview-scene { position: absolute; inset: 0; opacity: 0; pointer-events: none; transition: opacity 180ms ease; }
.invitation-preview-scene.is-ready { opacity: 1; }
.invitation-preview-copy { display: flex; flex-direction: column; gap: 7px; padding: 12px 8px 7px; }
.invitation-preview-copy > span:first-child { font: 15px/1.35 'PT Serif',Georgia,serif; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; }
.invitation-action { font-size: 11px; line-height: 1.4; display: flex; justify-content: space-between; opacity: .72; }
.invitation-action > span { display: inline-block; transition: transform 160ms ease; }
.invitation-preview:hover .invitation-action > span { transform: translate(2px,-2px); }
.invitation-preview:active { background: rgba(var(--invitation-paper),.94); }
@media (prefers-reduced-motion: reduce) { .exhibition-invitation *, .exhibition-invitation *::after { transition: none !important; } }
@media (max-width: 767px) { .exhibition-invitation { right:16px;bottom:calc(16px + env(safe-area-inset-bottom)); } .invitation-preview{display:none} }
@media (max-height:520px) { .invitation-preview {width:270px} }
</style>
